import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import type { ReactNode } from 'react';

type AuthAlertType = 'error' | 'warning' | 'info' | 'success';

const CONTAINER_CLASSES: Record<AuthAlertType, string> = {
  error: 'border-red-300 bg-red-50 text-red-800',
  warning: 'border-yellow-300 bg-yellow-50 text-yellow-800',
  info: 'border-blue-300 bg-blue-50 text-blue-800',
  success: 'border-green-300 bg-green-50 text-green-800',
};

const ICON_BAR_CLASSES: Record<AuthAlertType, string> = {
  error: 'bg-red-400 text-red-400',
  warning: 'bg-yellow-400 text-yellow-400',
  info: 'bg-blue-400 text-blue-400',
  success: 'bg-green-400 text-green-400',
};

const TITLE_CLASSES: Record<AuthAlertType, string> = {
  error: 'text-red-800',
  warning: 'text-yellow-800',
  info: 'text-blue-800',
  success: 'text-green-800',
};

const BODY_CLASSES: Record<AuthAlertType, string> = {
  error: 'text-red-700',
  warning: 'text-yellow-700',
  info: 'text-blue-700',
  success: 'text-green-700',
};

const ICONS: Record<AuthAlertType, typeof XCircleIcon> = {
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
  success: CheckCircleIcon,
};

type AuthAlertProps = {
  type?: AuthAlertType;
  title?: string;
  noBottomMargin?: boolean;
  children: ReactNode;
};

export function AuthAlert({ type = 'info', title, noBottomMargin, children }: AuthAlertProps) {
  const Icon = ICONS[type];

  return (
    <div
      className={cn(
        'flex overflow-hidden rounded-md border shadow',
        CONTAINER_CLASSES[type],
        !noBottomMargin && 'mb-6',
      )}
    >
      <div
        className={cn(
          'flex w-14.75 min-w-16 shrink-0 items-start justify-center text-white',
          ICON_BAR_CLASSES[type],
          title ? 'pt-4' : 'pt-3',
        )}
      >
        <Icon className="size-7 text-white" />
      </div>
      <div className="p-4">
        {title && <h3 className={cn('text-sm', TITLE_CLASSES[type])}>{title}</h3>}
        <p className={cn('text-sm', BODY_CLASSES[type])}>{children}</p>
      </div>
    </div>
  );
}
